import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import AnimeCardRow from "@/components/feature/AnimeCardRow";
import SearchBox from "@/components/feature/SearchBox";
import SectionTitle from "@/components/feature/SectionTitle";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickEntry from "@/pages/home/components/CategoryQuickEntry";
import HotList from "@/pages/home/components/HotList";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { formatUpdated, sortAnime } from "@/utils/anime";

const HERO_SLUGS = ["star-voyage-01", "spirit-sword-02", "academy-magic-15", "mecha-dawn-04"];

export default function Home() {
  usePageMeta(
    "动漫免费在观看 - 高清动漫在线观看平台",
    "动漫免费在观看为您提供丰富的动漫作品覆盖多种题材，支持在线观看最新热门动漫，让动漫爱好者轻松观看喜爱的动漫内容。",
    "动漫免费在观看,动漫在线观看,免费观看动漫,高清动漫,动画片,动漫视频,热门动漫",
  );

  const heroItems = HERO_SLUGS.map((slug) => animeList.find((item) => item.id === slug)).filter(
    (item): item is (typeof animeList)[number] => Boolean(item),
  );
  const latest = sortAnime(animeList, "latest");
  const popular = sortAnime(animeList, "popular");
  const newest = sortAnime(animeList, "newest");

  return (
    <AppLayout>
      <div className="flex min-h-full flex-col">
        <div className="flex-1 px-4 md:px-6 pt-5 md:pt-6">
          {/* 顶部：站点标题 + 搜索 */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
            <div className="min-w-0">
              <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-tight">
                动漫免费在观看
              </h1>
              <p className="mt-1 text-[12px] md:text-[13px] text-foreground-600">
                高清动漫 · 多题材覆盖 · 最新热门一站浏览
              </p>
            </div>
            <div className="w-full lg:max-w-md lg:ml-auto">
              <SearchBox />
            </div>
          </div>

          {/* 主体：左推荐栏 + 右内容展示 */}
          <div className="mt-5 grid grid-cols-1 xl:grid-cols-[264px_1fr] gap-5 md:gap-6">
            <aside className="hidden xl:flex flex-col gap-4 order-2 xl:order-1">
              <section className="rounded-lg border border-background-200 bg-background-100 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-1.5">
                    <i className="ri-flashlight-fill text-accent-500" />
                    编辑推荐
                  </h2>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900">
                    每日更新
                  </span>
                </div>
                <ul className="mt-3 space-y-2.5">
                  {newest.slice(0, 3).map((anime) => (
                    <li key={anime.id}>
                      <Link
                        to={`/detail/${anime.id}`}
                        className="flex gap-2.5 group cursor-pointer"
                      >
                        <span className="w-14 h-[74px] shrink-0 rounded-md overflow-hidden bg-background-200 block">
                          <img
                            src={anime.cover}
                            alt={`${anime.title} 封面海报`}
                            title={`${anime.title} - 高清动漫在线观看`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-heading text-[13px] font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {anime.title}
                          </span>
                          <span className="mt-1 flex flex-wrap gap-1">
                            {anime.categories.slice(0, 2).map((tag) => (
                              <span key={tag} className="px-1.5 py-0.5 rounded bg-background-50 border border-background-200 text-[10px] text-foreground-600">
                                {tag}
                              </span>
                            ))}
                          </span>
                          <span className="mt-1 block text-[10.5px] text-foreground-500">
                            {anime.year} · {anime.region} · {anime.status}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <HotList items={animeList} />

              <section className="rounded-lg bg-primary-950 p-4">
                <h2 className="font-heading text-sm font-bold text-background-50">按题材浏览</h2>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="px-2 py-1 rounded-full bg-primary-800 text-[11px] text-background-100 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-background-400">
                  集数列表为演示界面，暂不提供真实视频播放。
                </p>
              </section>
            </aside>

            <div className="order-1 xl:order-2 min-w-0">
              <HeroCarousel items={heroItems} />

              <div className="mt-6">
                <CategoryQuickEntry />
              </div>

              <section className="mt-7" aria-labelledby="recent-title">
                <SectionTitle
                  title="最新更新"
                  subtitle="最近一周更新与上线的动漫作品"
                  icon="ri-time-line"
                  moreTo="/list?sort=latest"
                  moreLabel="全部更新"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-5">
                  {latest.slice(0, 8).map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </section>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
            <section aria-labelledby="hot-anime-title">
              <SectionTitle
                title="热门动漫"
                subtitle="根据观看热度综合推荐"
                icon="ri-award-line"
                moreTo="/list?sort=popular"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-5">
                {popular.slice(0, 4).map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            </section>

            <section aria-labelledby="update-board-title" className="rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-center justify-between">
                <h2 id="update-board-title" className="font-heading text-base font-bold text-foreground-950 flex items-center gap-1.5">
                  <i className="ri-calendar-check-line text-accent-500" />
                  更新日历
                </h2>
                <span className="text-[10.5px] text-foreground-500">近 7 天</span>
              </div>
              <AnimeCardRow items={latest.slice(8, 12)} />
              <Link
                to="/list?sort=latest"
                className="mt-3 h-8 w-full rounded-md bg-secondary-100 text-secondary-900 text-[12px] flex items-center justify-center gap-1 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部更新
                <i className="ri-arrow-right-s-line" />
              </Link>
            </section>
          </div>

          <section
            className="mt-8 rounded-lg border border-background-200 bg-background-100 px-5 py-6 md:px-8 md:py-7"
            aria-labelledby="intro-title"
          >
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
              <div>
                <h2 id="intro-title" className="font-heading text-lg font-bold text-foreground-950">
                  关于动漫免费在观看
                </h2>
                <p className="mt-2 text-[13px] leading-relaxed text-foreground-600">
                  动漫免费在观看是一个专注动漫内容浏览的门户站点，覆盖科幻、奇幻、冒险、恋爱、热血、悬疑、治愈、校园等多种题材，
                  收录日本、中国、欧美、韩国多地区作品。你可以按题材、地区、年份与评分快速筛选，也可以直接搜索片名，
                  在详情页查看简介、集数列表与相关推荐，并把喜欢的作品收藏到自己的清单里。
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                  说明：本站集数列表为演示界面，仅展示信息结构，不提供真实视频播放、缓存或下载服务。
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/about"
                    className="h-8 px-3 rounded-md bg-primary-500 text-background-50 text-[12px] flex items-center gap-1 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-information-line" />
                    了解我们
                  </Link>
                  <Link
                    to="/about#feedback"
                    className="h-8 px-3 rounded-md border border-background-300 text-[12px] text-foreground-700 flex items-center gap-1 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line" />
                    提交反馈
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: animeList.length, label: "收录作品", icon: "ri-film-line" },
                  {
                    value: `${animeList.reduce((sum, item) => sum + item.episodes, 0)}+`,
                    label: "演示集数",
                    icon: "ri-play-list-line",
                  },
                  { value: categories.length, label: "题材分类", icon: "ri-apps-2-line" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md bg-background-50 border border-background-200 px-2 py-3 text-center">
                    <span className="w-7 h-7 mx-auto flex items-center justify-center text-accent-600">
                      <i className={`${stat.icon} text-lg`} />
                    </span>
                    <p className="mt-1 font-heading text-base font-bold text-foreground-950">{stat.value}</p>
                    <p className="text-[11px] text-foreground-500">{stat.label}</p>
                  </div>
                ))}
                <div className="col-span-3 rounded-md bg-background-50 border border-background-200 px-3 py-2.5">
                  <p className="text-[11px] text-foreground-500">最近更新</p>
                  <p className="mt-0.5 text-[12px] text-foreground-900">
                    {latest[0]?.title} · {formatUpdated(latest[0]?.updatedAt || "")}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
        <SiteFooter />
      </div>
    </AppLayout>
  );
}